import { requestHeaders } from '../helpers/csrf';
import { readResponsePayload, resolveErrorMessage } from '../helpers/error-handler';
import { hasMinLength, isPasswordConfirmationValid } from '../helpers/validation-helper';

class ParameterizedPasswordModal {
    constructor(config = {}) {
        this.dialogService = globalThis.FlyServices?.dialog;
        this.notificationService = globalThis.FlyServices?.notify;
        this._dialogRef = null;
        this._submitting = false;

        this.config = {
            className: config.className ?? 'nd-user-password-dialog',
            wrapperClass: config.wrapperClass ?? 'nd-user-password',
            dataPrefix: config.dataPrefix ?? 'pwd',
            inputIdPrefix: config.inputIdPrefix ?? 'nd-pwd',
            updateRouteField: config.updateRouteField ?? 'updatePassword',
            defaultSuccessMessage: config.defaultSuccessMessage ?? 'Senha atualizada com sucesso.',
            contextIcon: config.contextIcon ?? 'ph ph-user-circle',
            requireCurrentPassword: false,
            ...config,
        };
    }

    open(options = {}) {
        const { entityName = '', entityEmail = '', updateUrl = '' } = options;

        if (!this.dialogService) return;

        this._submitting = false;
        const root = this._buildLayout(entityName, entityEmail);

        this._dialogRef = this.dialogService.open({
            content: root,
            size: 'sm',
            position: 'center',
            className: this.config.className,
            showClose: false,
            animation: 'scale',
            closeOnOverlayClick: true,
        });

        this._bindEvents(root, updateUrl);
        this._focusFirstField(root);
    }

    openFromRow(row = {}) {
        const updateUrl = row?.routes?.[this.config.updateRouteField] ?? '';
        return this.open({
            entityName: row?.nome ?? '',
            entityEmail: row?.email ?? '',
            updateUrl,
        });
    }

    _bindEvents(root, updateUrl) {
        root.querySelector(this._sel('close'))?.addEventListener('click', () => this._dialogRef?.close());
        root.querySelector(this._sel('cancel'))?.addEventListener('click', () => this._dialogRef?.close());

        root.querySelector(this._sel('form'))?.addEventListener('submit', (e) => {
            e.preventDefault();
            this._submit(root, updateUrl);
        });

        root.querySelectorAll(this._sel('toggle')).forEach((btn) => {
            btn.addEventListener('click', () => this._toggleVisibility(btn));
        });
    }

    _focusFirstField(root) {
        requestAnimationFrame(() => {
            const first = this.config.requireCurrentPassword
                ? this._sel('current-password')
                : this._sel('password');
            root.querySelector(first)?.focus();
        });
    }

    _validate(root) {
        const password     = root.querySelector(this._sel('password'))?.value ?? '';
        const confirmation = root.querySelector(this._sel('confirmation'))?.value ?? '';

        this._clearErrors(root);

        if (this.config.requireCurrentPassword) {
            const current = root.querySelector(this._sel('current-password'))?.value ?? '';
            if (!current) {
                this._showFieldError(root, 'current-password', 'A senha atual é obrigatória.');
                return false;
            }
        }

        if (!password) {
            this._showFieldError(root, 'password', 'A nova senha é obrigatória.');
            return false;
        }

        if (!hasMinLength(password, 8)) {
            this._showFieldError(root, 'password', 'A senha deve ter pelo menos 8 caracteres.');
            return false;
        }

        if (!confirmation) {
            this._showFieldError(root, 'confirmation', 'A confirmação da senha é obrigatória.');
            return false;
        }

        if (!isPasswordConfirmationValid(password, confirmation)) {
            this._showFieldError(root, 'confirmation', 'As senhas não conferem.');
            return false;
        }

        return true;
    }

    async _submit(root, updateUrl) {
        if (this._submitting) return;
        if (!updateUrl) return;
        if (!this._validate(root)) return;

        this._setSubmitting(root, true);

        try {
            const password     = root.querySelector(this._sel('password'))?.value ?? '';
            const confirmation = root.querySelector(this._sel('confirmation'))?.value ?? '';

            const formData = new FormData();
            formData.append('_method', 'PATCH');
            formData.append('password', password);
            formData.append('password_confirmation', confirmation);

            if (this.config.requireCurrentPassword) {
                const current = root.querySelector(this._sel('current-password'))?.value ?? '';
                formData.append('current_password', current);
            }

            const response = await fetch(updateUrl, {
                method: 'POST',
                headers: requestHeaders(),
                body: formData,
            });

            const payload = await readResponsePayload(response);

            if (response.ok) {
                const msg = typeof payload === 'string' ? payload : this.config.defaultSuccessMessage;
                this.notificationService?.success?.(msg);
                this._dialogRef?.close();
                return;
            }

            if (response.status === 422 && payload?.errors) {
                if (payload.errors.current_password) {
                    this._showFieldError(root, 'current-password', payload.errors.current_password[0]);
                }
                if (payload.errors.password) {
                    this._showFieldError(root, 'password', payload.errors.password[0]);
                }
                if (payload.errors.password_confirmation) {
                    this._showFieldError(root, 'confirmation', payload.errors.password_confirmation[0]);
                }
                this._setSubmitting(root, false);
                return;
            }

            this.notificationService?.error?.(resolveErrorMessage(payload, 'Erro ao atualizar a senha.'));
            this._setSubmitting(root, false);
        } catch (error) {
            this.notificationService?.error?.(resolveErrorMessage(error, 'Erro de conexão ao atualizar a senha.'));
            this._setSubmitting(root, false);
        }
    }

    _buildLayout(entityName, entityEmail) {
        const wrapper = document.createElement('div');
        wrapper.className = this.config.wrapperClass;

        const safeName = this._escapeHtml(entityName);
        const safeEmail = this._escapeHtml(entityEmail);
        const nameHtml = safeName
            ? `<span class="$typo-label text-typography-primary">${safeName}</span>`
            : '';
        const emailHtml = safeEmail
            ? `<span class="${this.config.wrapperClass}__context-email">${safeEmail}</span>`
            : '';
        const context = (safeName || safeEmail)
            ? `<div class="${this.config.wrapperClass}__context">
                    <i class="${this.config.contextIcon}" aria-hidden="true"></i>
                    <div class="${this.config.wrapperClass}__context-info">
                    ${emailHtml}
                    ${nameHtml}
                    </div>
               </div>`
            : '';

        wrapper.innerHTML = `
            <div class="${this.config.wrapperClass}__header">
                <div class="${this.config.wrapperClass}__title-wrap">
                    <h2 class="${this.config.wrapperClass}__title">Alterar senha</h2>
                </div>
                <button type="button" class="${this.config.wrapperClass}__close" aria-label="Fechar" ${this._attr('close')}>
                    <i class="ph ph-x" aria-hidden="true"></i>
                </button>
            </div>

            ${context}

            <form class="${this.config.wrapperClass}__form" ${this._attr('form')} autocomplete="off" novalidate>
                <div class="${this.config.wrapperClass}__fields">
                    ${this.config.requireCurrentPassword ? `
                    <div class="${this.config.wrapperClass}__field" ${this._attr('field-current-password')}>
                        <label class="typo-label" for="${this.config.inputIdPrefix}-current-password">Senha atual</label>
                        <div class="${this.config.wrapperClass}__input-wrap">
                            <input id="${this.config.inputIdPrefix}-current-password" type="password" class="${this.config.wrapperClass}__input" ${this._attr('current-password')} placeholder="Digite a senha atual" autocomplete="current-password" />
                            <button type="button" class="${this.config.wrapperClass}__eye-btn" ${this._attr('toggle')} data-target="${this.config.inputIdPrefix}-current-password" aria-label="Mostrar senha">
                                <i class="ph ph-eye" aria-hidden="true"></i>
                            </button>
                        </div>
                        <span class="${this.config.wrapperClass}__error" ${this._attr('error-current-password')} role="alert"></span>
                    </div>
                    ` : ''}

                    <div class="${this.config.wrapperClass}__field" ${this._attr('field-password')}>
                        <label class="typo-label" for="${this.config.inputIdPrefix}-password">Nova senha</label>
                        <div class="${this.config.wrapperClass}__input-wrap">
                            <input id="${this.config.inputIdPrefix}-password" type="password" class="${this.config.wrapperClass}__input" ${this._attr('password')} placeholder="Digite a nova senha" autocomplete="new-password" />
                            <button type="button" class="${this.config.wrapperClass}__eye-btn" ${this._attr('toggle')} data-target="${this.config.inputIdPrefix}-password" aria-label="Mostrar senha">
                                <i class="ph ph-eye" aria-hidden="true"></i>
                            </button>
                        </div>
                        <span class="${this.config.wrapperClass}__error" ${this._attr('error-password')} role="alert"></span>
                    </div>

                    <div class="${this.config.wrapperClass}__field" ${this._attr('field-confirmation')}>
                        <label class="typo-label" for="${this.config.inputIdPrefix}-confirmation">Confirme a nova senha</label>
                        <div class="${this.config.wrapperClass}__input-wrap">
                            <input id="${this.config.inputIdPrefix}-confirmation" type="password" class="${this.config.wrapperClass}__input" ${this._attr('confirmation')} placeholder="Repita a nova senha" autocomplete="new-password" />
                            <button type="button" class="${this.config.wrapperClass}__eye-btn" ${this._attr('toggle')} data-target="${this.config.inputIdPrefix}-confirmation" aria-label="Mostrar senha">
                                <i class="ph ph-eye" aria-hidden="true"></i>
                            </button>
                        </div>
                        <span class="${this.config.wrapperClass}__error" ${this._attr('error-confirmation')} role="alert"></span>
                    </div>
                </div>

                <div class="${this.config.wrapperClass}__actions">
                    <button type="button" class="${this.config.wrapperClass}__cancel" ${this._attr('cancel')}>Cancelar</button>
                    <button type="submit" class="${this.config.wrapperClass}__submit" ${this._attr('submit')}>Atualizar senha</button>
                </div>
            </form>
        `;

        return wrapper;
    }

    _showFieldError(root, fieldName, message) {
        const errorEl = root.querySelector(this._sel(`error-${fieldName}`));
        if (errorEl) {
            errorEl.textContent = message;
            errorEl.style.display = 'block';
        }

        const hintEl = root.querySelector(this._sel(`hint-${fieldName}`));
        if (hintEl) hintEl.style.display = 'none';

        const fieldEl = root.querySelector(this._sel(`field-${fieldName}`));
        fieldEl?.classList.add(`${this.config.wrapperClass}__field--error`);
    }

    _clearErrors(root) {
        root.querySelectorAll(this._selLike('error')).forEach((el) => {
            el.textContent = '';
            el.style.display = 'none';
        });

        root.querySelectorAll(this._selLike('hint')).forEach((el) => {
            el.style.display = '';
        });

        root.querySelectorAll(`.${this.config.wrapperClass}__field--error`).forEach((el) => {
            el.classList.remove(`${this.config.wrapperClass}__field--error`);
        });
    }

    _toggleVisibility(btn) {
        const targetId = btn.dataset.target;
        const input = document.getElementById(targetId);
        if (!input) return;

        const isPassword = input.type === 'password';
        input.type = isPassword ? 'text' : 'password';

        const icon = btn.querySelector('i');
        if (icon) {
            icon.className = isPassword ? 'ph ph-eye-slash' : 'ph ph-eye';
        }

        btn.setAttribute('aria-label', isPassword ? 'Ocultar senha' : 'Mostrar senha');
    }

    _setSubmitting(root, loading) {
        this._submitting = loading;

        const submitBtn = root.querySelector(this._sel('submit'));
        const cancelBtn = root.querySelector(this._sel('cancel'));
        const inputs = root.querySelectorAll(`.${this.config.wrapperClass}__input`);

        if (submitBtn) {
            submitBtn.disabled = loading;
            submitBtn.textContent = loading ? 'Atualizando...' : 'Atualizar senha';
        }

        if (cancelBtn) cancelBtn.disabled = loading;
        inputs.forEach((input) => { input.disabled = loading; });
    }

    _attr(name) {
        return `data-${this.config.dataPrefix}-${name}`;
    }

    _sel(name) {
        return `[data-${this.config.dataPrefix}-${name}]`;
    }

    _selLike(prefix) {
        const base = `[data-${this.config.dataPrefix}-${prefix}-password], [data-${this.config.dataPrefix}-${prefix}-confirmation]`;
        return this.config.requireCurrentPassword
            ? `${base}, [data-${this.config.dataPrefix}-${prefix}-current-password]`
            : base;
    }

    _escapeHtml(value) {
        return String(value ?? '')
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#39;');
    }
}

export default ParameterizedPasswordModal;
